import { type PageUpdatePreviewRow } from '@elementary/common-ui/components/AiChat';
import {
  capitalizeWords,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { coerceWizardStringList } from 'testManagement/utils/utils';

const MAX_PREVIEW_VALUE_LENGTH = 80;

const WIZARD_FIELD_LABELS: Record<string, string> = {
  resources: 'Selected assets',
  resourceType: 'Asset type',
  testType: 'Test',
  yamlTemplateName: 'YAML test',
  yaml: 'Configuration',
  description: 'Description',
  name: 'Name',
  owners: 'Owners',
  tags: 'Tags',
  severity: 'Severity',
  limit: 'Limit',
  where: 'Where',
  whereClause: 'Where',
  shouldStoreFailures: 'Store failures',
  testNameOverride: 'Name override',
  columnGroup: 'Columns',
  values: 'Values',
  anomalyConfig: 'Detection settings',
  sensitivity: 'Sensitivity',
  trainingPeriod: 'Training period',
  detectionPeriod: 'Detection period',
  failureThreshold: 'Failure threshold',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const clip = (value: string): string =>
  value.length > MAX_PREVIEW_VALUE_LENGTH
    ? `${value.slice(0, MAX_PREVIEW_VALUE_LENGTH).trimEnd()}…`
    : value;

const formatWizardFieldLabel = (field: string): string =>
  WIZARD_FIELD_LABELS[field] ??
  capitalizeWords(field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '));

const formatResource = (value: unknown): string => {
  if (typeof value === 'string') {
    const parts = value.split(/[./]/);
    return parts[parts.length - 1] || value;
  }
  if (isRecord(value)) {
    if (typeof value.name === 'string') {
      return value.name;
    }
    if (typeof value.id === 'string') {
      return formatResource(value.id);
    }
  }
  return clip(JSON.stringify(value));
};

const formatSeverityCondition = (
  prefix: string,
  condition: unknown,
  fallback?: string,
): string | undefined => {
  if (isRecord(condition)) {
    const operator =
      typeof condition.operator === 'string' ? condition.operator : undefined;
    const threshold =
      typeof condition.value === 'number' || typeof condition.value === 'string'
        ? condition.value
        : undefined;
    if (operator !== undefined && threshold !== undefined) {
      return `${prefix} if failures ${operator} ${threshold}`;
    }
  }
  if (typeof fallback === 'string' && fallback.trim()) {
    return `${prefix} if ${fallback}`;
  }
  return undefined;
};

const formatSeverityValue = (
  value: Record<string, unknown>,
): string | undefined => {
  const errorIf =
    typeof value.errorIf === 'string'
      ? value.errorIf
      : typeof value.error_if === 'string'
      ? value.error_if
      : undefined;
  const warnIf =
    typeof value.warnIf === 'string'
      ? value.warnIf
      : typeof value.warn_if === 'string'
      ? value.warn_if
      : undefined;
  const parts = [
    formatSeverityCondition('error', value.conditionalError, errorIf),
    formatSeverityCondition('warn', value.conditionalWarning, warnIf),
  ].filter((part): part is string => !!part);
  if (parts.length > 0) {
    return `conditional, ${parts.join(', ')}`;
  }
  if (
    typeof value.type === 'string' &&
    value.type.toLowerCase() === 'conditional'
  ) {
    return 'conditional';
  }
  if (typeof value.type === 'string') {
    return value.type;
  }
  return undefined;
};

const formatPeriod = (value: Record<string, unknown>): string | undefined => {
  if (typeof value.period !== 'string' || typeof value.count !== 'number') {
    return undefined;
  }
  return pluralizeString(value.count, value.period, `${value.period}s`);
};

const formatWizardFieldValue = (
  field: string,
  value: unknown,
): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    if (field === 'resources' || field === 'owners' || field === 'tags') {
      const list = coerceWizardStringList(value);
      if (list) {
        return formatWizardFieldValue(field, list);
      }
    }
    return clip(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'None';
    }
    if (field === 'resources') {
      const names = value.map(formatResource);
      return names.length <= 3
        ? names.join(', ')
        : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
    }
    if (value.every((item) => typeof item === 'string')) {
      return clip(value.join(', '));
    }
    return pluralizeString(value.length, 'value', 'values');
  }
  if (isRecord(value)) {
    if (field === 'severity') {
      const severity = formatSeverityValue(value);
      if (severity) {
        return severity;
      }
    }
    const period = formatPeriod(value);
    if (period) {
      return period;
    }
    if (typeof value.type === 'string' && Object.keys(value).length <= 2) {
      return value.type;
    }
    const nested = Object.entries(value).flatMap(
      ([nestedField, nestedValue]) => {
        const formatted = formatWizardFieldValue(nestedField, nestedValue);
        return formatted
          ? [`${formatWizardFieldLabel(nestedField)}: ${formatted}`]
          : [];
      },
    );
    return nested.length > 0
      ? clip(nested.join(', '))
      : clip(JSON.stringify(value));
  }
  return String(value);
};

export const previewWizardPageUpdate = (
  proposed: Record<string, unknown>,
): PageUpdatePreviewRow[] =>
  Object.entries(proposed).flatMap(([field, value]) => {
    const formatted = formatWizardFieldValue(field, value);
    if (!formatted) {
      return [];
    }
    return [{ field, label: formatWizardFieldLabel(field), value: formatted }];
  });

type Rect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export const isRectFullyVisible = (item: Rect, bounds: Rect) =>
  item.top >= bounds.top &&
  item.right <= bounds.right &&
  item.bottom <= bounds.bottom &&
  item.left >= bounds.left;

export const applyGeneratedDescription = (
  methods: {
    getValues: (name: 'description') => string | undefined;
    setValue: (name: 'description', value: string) => void;
  },
  generated: string,
  lastGeneratedRef: { current: string },
) => {
  const current = methods.getValues('description') ?? '';
  const shouldSync = current === '' || current === lastGeneratedRef.current;
  lastGeneratedRef.current = generated;
  if (shouldSync) {
    methods.setValue('description', generated);
  }
};
